import EntityAvatar from './EntityAvatar';
import BranchLocationField from './BranchLocationField';

function EntityForm({
  values,
  errors,
  isSaving,
  onChange,
  onClearPhoto,
  onSubmit,
  onCancel,
  entityLabel = 'Supplier',
}) {
  const entityLabelLower = entityLabel.toLowerCase();
  const showsSacksMark = entityLabel === 'Supplier';
  const showsBranchLocations = entityLabel === 'Customer';

  return (
    <form
      className="rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] p-5 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.03)] sm:p-6"
      onSubmit={onSubmit}
    >
      <div className="mb-6 flex flex-col gap-4 border-b border-[var(--theme-border-soft)] pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
            {entityLabel} Module
          </p>
          <h2 className="m-0 mt-2 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
            Add {entityLabel} Profile
          </h2>
          <p className="m-0 mt-2 text-[0.95rem] text-[var(--theme-text-muted)]">
            Create a new {entityLabelLower} record for contact tracking and account management.
          </p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6">
        <div className="flex flex-col items-center rounded-[22px] border border-[#ebe2d9] bg-white p-5 text-center shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.025)]">
          <EntityAvatar
            photoUrl={values.photoUrl}
            name={values.companyName}
            entityLabel={entityLabel}
            className="h-36 w-36 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]"
            textClassName="text-[2.9rem] font-semibold"
          />
          <strong className="mt-4 text-[0.98rem] font-medium text-[#2d2723]">
            {entityLabel} Photo
          </strong>
          <span className="mt-1 text-[0.88rem] leading-6 text-[var(--theme-text-muted)]">
            Add a {entityLabelLower} photo if available. You can also leave this blank for now.
          </span>
          <label className="mt-4 inline-flex cursor-pointer items-center rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-4 py-2 text-[0.88rem] font-medium text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)]">
            Upload Photo
            <input
              className="sr-only"
              type="file"
              name="photoUrl"
              accept="image/*"
              onChange={onChange}
            />
          </label>
          {values.photoUrl ? (
            <button
              className="mt-3 inline-flex items-center rounded-full border border-[var(--theme-border-soft)] bg-white px-4 py-2 text-[0.84rem] font-medium text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onClearPhoto}
              disabled={isSaving}
            >
              Remove Photo
            </button>
          ) : null}
        </div>

        <div className="grid gap-5">
          <section className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.025)] sm:p-5">
            <div className="mb-4">
              <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
                Company Information
              </h3>
              <p className="m-0 mt-2 text-[0.9rem] text-[var(--theme-text-muted)]">
                Primary business and contact details for {entityLabelLower} identification.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormInput
                label="Company Name"
                name="companyName"
                value={values.companyName}
                error={errors.companyName}
                onChange={onChange}
                placeholder="Enter company name"
              />
              <FormInput
                label="Contact Number (optional)"
                name="contactNumber"
                value={values.contactNumber}
                error={errors.contactNumber}
                onChange={onChange}
                placeholder="09XXXXXXXXX"
              />
              {showsSacksMark ? (
                <FormInput
                  label="Sacks Mark"
                  name="sacksMark"
                  value={values.sacksMark}
                  error={errors.sacksMark}
                  onChange={onChange}
                  placeholder="Auto-generated from company name"
                />
              ) : null}
              {showsBranchLocations ? (
                <BranchLocationField
                  value={values.branchLocations}
                  error={errors.branchLocations}
                  onChange={onChange}
                  disabled={isSaving}
                  className="md:col-span-2"
                />
              ) : (
                <FormInput
                  label="Address"
                  name="address"
                  value={values.address}
                  error={errors.address}
                  onChange={onChange}
                  placeholder={`Enter ${entityLabelLower} address`}
                  className={showsSacksMark ? '' : 'md:col-span-2'}
                />
              )}
            </div>
          </section>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap justify-end gap-3">
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-7 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#d8cec4] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
          type="submit"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : `Save ${entityLabel}`}
        </button>
      </div>
    </form>
  );
}

function FormInput({
  label,
  name,
  value,
  error,
  onChange,
  inputMode,
  placeholder,
  className,
}) {
  return (
    <label className={`grid self-start gap-2 ${className || ''}`}>
      <span className="text-[0.88rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`h-[48px] rounded-[16px] border bg-[var(--theme-surface-warm)] px-4 text-[0.95rem] outline-none transition placeholder:text-[#b3a79c] ${error
          ? 'border-[#d78b7d] focus:border-[#c76552]'
          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
          }`}
        name={name}
        value={value}
        onChange={onChange}
        inputMode={inputMode}
        placeholder={placeholder}
      />
      <span
        className={`min-h-[16px] text-[0.8rem] leading-4 ${
          error ? 'text-[#bb5745]' : 'invisible text-transparent'
        }`}
      >
        {error || '-'}
      </span>
    </label>
  );
}

export default EntityForm;
